import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { fieldMeetCertainCriteriaStyles } from '../styles';
import { supportedWorkItemUserFieldValues, useUserFieldHook } from './use-user-field-hook';
import { useAutomationRuleConditions } from '../../use-conditions';
import { ConditionItemType, FieldMeetCertainCriteriaType } from '../../types';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  conditionId: string;
  validationError: string | null;
	isMultiSelect?: boolean;
}

const clearTimeoutHandler = (debounce: NodeJS.Timeout | null) => {
	if (debounce) {
		clearTimeout(debounce);
	}
};

export default function UserFieldInput(props: Props) {
	const classes = fieldMeetCertainCriteriaStyles();
	const userFieldHook = useUserFieldHook();
	const {conditions, setConditions, resetValidationErrors} = useAutomationRuleConditions();
  
	let debounce: NodeJS.Timeout | null = null;

	const [loading, setLoading] = useState(false);
	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const [initialOptions, setInitialOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: FieldMeetCertainCriteriaType = useMemo(() => {
		const foundConditions = conditions.find(item => item.id === props.conditionId) as ConditionItemType;
		return foundConditions!.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType;
	}, [conditions, props.conditionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		clearTimeoutHandler(debounce);
	
		if (value === '') {
			userFieldHook.getIdentityOptions().then((resultOptions) => {
				setOptions([
					...resultOptions,
					...supportedWorkItemUserFieldValues
				]);
				setLoading(false);
			});
		}
		
		if (value !== '') {
			setLoading(true);
			debounce = setTimeout(() => {
				userFieldHook.searchIdentityOptions(value)
					.then((resultOptions) => {
						setOptions([
							...resultOptions,
						]);

						const itIsNotInInitialOptions: Array<{label: string; value: string}> = resultOptions.filter((resultOption) => {
							return !initialOptions.some(initialOptions => initialOptions.value === resultOption.value);
						});

						setInitialOptions((oldInitialOptions) => [...oldInitialOptions, ...itIsNotInInitialOptions]);
					})
					.finally(() => {
						setLoading(false);
					});
			}, 750);
		}
	}, [initialOptions, options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundCondition = conditions.find(item => item.id === props.conditionId);

			if (props.isMultiSelect) {
				setStatus('searching');
				setText('');
			}

			if (!props.isMultiSelect) {
				setStatus('selected');
			}
	
			if (foundCondition) {
				if (props.isMultiSelect) {
					setConditions(oldConditions => {
						const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
						const others = oldConditions.filter(item => item.id !== props.conditionId);

						const selectedValues = current.fieldMeetCertainCriteria!.userFieldValue.includes(value) ?
							current.fieldMeetCertainCriteria!.userFieldValue.filter(item => item !== value) :
							[...current.fieldMeetCertainCriteria!.userFieldValue, value];

						return [...others, {
							...current, fieldMeetCertainCriteria: {
								...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
								userFieldValue: selectedValues
							}
						}];
					});
				}

				if (!props.isMultiSelect) {
					setConditions(oldConditions => {
						const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
						const others = oldConditions.filter(item => item.id !== props.conditionId);
		
						return [...others, {
							...current, fieldMeetCertainCriteria: {
								...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
								userFieldValue: [value]
							}
						}];
					});
				}
			}
		}
	}, [
		props.isMultiSelect,
		initialOptions
	]);

	const clearOptionSelect = useCallback(() => {
		const foundCondition = conditions.find(item => item.id === props.conditionId);
		setStatus('selected');

		if (foundCondition) {
			setConditions(oldConditions => {
				const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
				const others = oldConditions.filter(item => item.id !== props.conditionId);

				return [...others, {
					...current, fieldMeetCertainCriteria: {
						...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
						userFieldValue: []
					}
				}];
			});
		}
	}, []);

	const loadInitialOptions = useCallback(async () => {
		if (initialOptions.length > 0) {
			setOptions(initialOptions);
		}

		if (!initialOptions.length) {
			userFieldHook.getIdentityOptions().then((resultOptions) => {
				const list = [
					...resultOptions,
					...supportedWorkItemUserFieldValues
				];
	
				setInitialOptions(list);
				setOptions(list);
			});
		}
	}, [initialOptions]);
  
	const valueRender = useMemo(() => {
		if (current && current.userFieldValue && status === 'selected') {
			return options.filter((item) => current.userFieldValue.includes(item.value)).map((item) => item.label).join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current, options, status]);

	const selectedOptions = useMemo(() => {
		const filteredValues = options.filter(item => current.userFieldValue.includes(item.value));

		if (current.userFieldValue && filteredValues.length > 0) {
			return filteredValues.map(item => item.value);
		}

		return [];
	}, [current, options]);

	useEffect(() => {
		setOnInitialLoading(true);
		const foundCondition = conditions.find(item => item.id === props.conditionId);

		const asyncHandler = async () => {
			if(
				foundCondition && 
				foundCondition?.fieldMeetCertainCriteria && 
				foundCondition?.fieldMeetCertainCriteria?.inputValueType === 'user-field' &&
				!foundCondition.fieldMeetCertainCriteria.userFieldValue
			) {
				setOnInitialLoading(true);
				await loadInitialOptions();
				setOnInitialLoading(false);
			}

			if (
				foundCondition && 
				foundCondition?.fieldMeetCertainCriteria && 
				foundCondition?.fieldMeetCertainCriteria?.inputValueType === 'user-field' &&
				foundCondition.fieldMeetCertainCriteria.userFieldValue
			) {
				const resultOptions = await userFieldHook.getIdentityOptions();
				const list = [
					...resultOptions,
					...supportedWorkItemUserFieldValues
				];
				
				await Promise.allSettled(
					foundCondition
						.fieldMeetCertainCriteria
						.userFieldValue
						.filter((item) => !supportedWorkItemUserFieldValues.some((supportedItem) => supportedItem.value === item))
						.map(async (item) => {
							const result = await userFieldHook.getIdentityById(item);
							if (list.every(item => item.value !== result.value) && result) {
								list.push(result);
							}
						})
				);

				setOptions(list);

			}

			setOnInitialLoading(false);
		};

		asyncHandler();
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable={!props.isMultiSelect}
				clearIcon={(
					<ClearebleButtonInput
						onClick={clearOptionSelect}
					/>
				)}
				inputMode='search'
				autoComplete='off'
				multiselect={props.isMultiSelect}
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onFocus={(ev) => {
					ev.preventDefault();
					setStatus('searching');
					setText('');
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					loadInitialOptions();
				}}
				multiple
			>
				<div style={{maxHeight: 240}}>
					{!loading && options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
					{loading && <Option value='loading'>Loading...</Option>}
				</div>
			</Combobox>
		</Field>
	);
}